import { applyCartItemQuantity, withGroupedCart } from '@/utilities/cart';
import { isUserLogin } from '@/utilities/CheckUserIsLogin';
import { groupCartContent } from '@/utilities/groupByProviderAndFulfillment';

import { CartStoreType } from './models';

import {
    addOfferToCartService,
    deleteOfferFromCartService,
    getCartService,
    removeCartCouponService,
    setCartCouponService,
    updateCartService,
    updateFullfilmentCartItemService
} from '@/client/services/vendor/methods';
import {
    addToCartBodyParamType,
    SetCartCouponQueryParam,
    UpdateCartItemQueryParamsType,
    UpdateFullfilmentCartItemQueryParamsType
} from '@/client/services/vendor/models';
import { isAxiosError } from 'axios';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
export const useCartStore = create<CartStoreType>()(
    immer((set, get) => ({
        cart: null,
        groupedCart: null,
        isUpdateFullfilmentCartItemLoading: false,
        fulfillmentErrors: {},
        isAddOfferToCartLoading: false,
        iaLoading: true,
        isUpdateCartItemLoading: false,
        isCouponCodeUpdateLoading: false,
        UpdateCartItemId: null,

        clearFulfillmentErrors: (itemIds: string[]) => {
            set((state) => {
                for (const itemId of itemIds) {
                    delete state.fulfillmentErrors[itemId];
                }
            });
        },

        getCartProducts: async () => {
            if (!isUserLogin()) {
                set((state) => {
                    state.cart = null;
                    state.groupedCart = null;
                    state.iaLoading = false;
                });

                return;
            }

            set((state) => {
                state.iaLoading = true;
            });

            try {
                const response = await getCartService();
                const carts = response?.data?.content || [];
                const cart = carts.length ? carts[0] : null;

                set((state) => {
                    state.cart = cart;
                    state.groupedCart = cart ? groupCartContent(cart) : null;
                });
            } catch {
                set((state) => {
                    state.cart = null;
                    state.groupedCart = null;
                });
            } finally {
                set((state) => {
                    state.iaLoading = false;
                });
            }
        },

        refreshCartById: async (cartId: string) => {
            set((state) => {
                state.iaLoading = true;
            });
            try {
                const response = await getCartService();
                const cart = response.data.content.find((entry) => entry.cartId === cartId);
                if (!cart) throw new Error('Cart is unavailable');
                set((state) => {
                    const next = withGroupedCart(cart);
                    state.cart = next.cart;
                    state.groupedCart = next.groupedCart;
                });
                return cart;
            } finally {
                set((state) => {
                    state.iaLoading = false;
                });
            }
        },

        addOfferToCart: async (bodyParam: addToCartBodyParamType, vendorName: string, productName: string, vendorUuid: string) => {
            set((state) => {
                state.iaLoading = true;
                state.isAddOfferToCartLoading = true;
            });
            try {
                const response = await addOfferToCartService(bodyParam);
                const cart = response?.data?.content;

                set((state) => {
                    state.cart = cart ?? null;
                    state.groupedCart = cart ? groupCartContent(cart) : null;
                });
            } finally {
                set((state) => {
                    state.iaLoading = false;
                    state.isAddOfferToCartLoading = false;
                });
            }
        },

        updateCartItem: async (queryParam: UpdateCartItemQueryParamsType) => {
            const previousCart = get().cart;

            set((state) => {
                state.isUpdateCartItemLoading = true;
                state.UpdateCartItemId = queryParam.id;

                if (previousCart) {
                    const next = withGroupedCart(applyCartItemQuantity(previousCart, queryParam.id, queryParam.quantity));
                    state.cart = next.cart;
                    state.groupedCart = next.groupedCart;
                }
            });

            try {
                const response = await updateCartService(queryParam);
                const cart = response?.data?.content ?? null;

                set((state) => {
                    const next = withGroupedCart(cart);
                    state.cart = next.cart;
                    state.groupedCart = next.groupedCart;
                });
            } catch (error) {
                set((state) => {
                    const next = withGroupedCart(previousCart);
                    state.cart = next.cart;
                    state.groupedCart = next.groupedCart;
                });
                throw error;
            } finally {
                set((state) => {
                    state.isUpdateCartItemLoading = false;
                    state.UpdateCartItemId = null;
                });
            }
        },

        deleteCartItem: async (queryParam: UpdateCartItemQueryParamsType) => {
            set((state) => {
                state.isUpdateCartItemLoading = true;
                state.UpdateCartItemId = queryParam.id;
            });

            try {
                const response = await deleteOfferFromCartService(queryParam);
                const cart = response?.data?.content ?? null;

                set((state) => {
                    state.cart = cart[0];
                    state.groupedCart = cart ? groupCartContent(cart[0]) : null;
                });
            } finally {
                set((state) => {
                    state.isUpdateCartItemLoading = false;
                    state.UpdateCartItemId = null;
                });
            }
        },

        updateFullfilmentCartItem: async (queryParam: UpdateFullfilmentCartItemQueryParamsType) => {
            set((state) => {
                state.isUpdateFullfilmentCartItemLoading = true;
                delete state.fulfillmentErrors[queryParam.id];
            });

            try {
                const response = await updateFullfilmentCartItemService(queryParam);
                const cart = response?.data?.content;

                set((state) => {
                    state.cart = cart ?? null;
                    state.groupedCart = cart ? groupCartContent(cart) : null;
                });
            } catch (error) {
                const apiError = isAxiosError<{ error?: { code?: string; message?: string } }>(error) ? error.response?.data?.error : null;
                const message =
                    apiError?.code === 'LOCATION_NOT_ELIGIBLE'
                        ? 'آدرس انتخاب‌شده خارج از محدوده ارسال این فروشنده است. لطفاً آدرس دیگری انتخاب کنید.'
                        : apiError?.message || 'ثبت اطلاعات ارسال انجام نشد. لطفاً دوباره آدرس تحویل را انتخاب کنید.';

                set((state) => {
                    state.fulfillmentErrors[queryParam.id] = message;
                });

                throw error;
            } finally {
                set((state) => {
                    state.isUpdateFullfilmentCartItemLoading = false;
                });
            }
        },

        addCouponCode: async (cartId: string, queryParam: SetCartCouponQueryParam) => {
            set((state) => {
                state.isCouponCodeUpdateLoading = true;
            });

            try {
                const response = await setCartCouponService(cartId, queryParam);
                const cart = response?.data?.content;

                set((state) => {
                    state.cart = cart ?? null;
                    state.groupedCart = cart ? groupCartContent(cart) : null;
                });
            } finally {
                set((state) => {
                    state.isCouponCodeUpdateLoading = false;
                });
            }
        },

        removeCouponCode: async (cartId: string) => {
            set((state) => {
                state.isCouponCodeUpdateLoading = true;
            });

            try {
                const response = await removeCartCouponService(cartId);
                const cart = response?.data?.content;

                set((state) => {
                    state.cart = cart ?? null;
                    state.groupedCart = cart ? groupCartContent(cart) : null;
                });
            } finally {
                set((state) => {
                    state.isCouponCodeUpdateLoading = false;
                });
            }
        }
    }))
);
